import { useState, useEffect } from "react";
import firebase from "api/firebase/firebase";

/**
 * The customization config, or null while it loads.
 *
 * Some 25 components call this hook. Each instance used to start at null and
 * fetch on mount, so every one of them rendered twice at startup, and with an
 * inline REACT_APP_CUSTOM_CONFIG each got a freshly parsed object (no two
 * consumers could share a `[config]` effect or memo). The config is now read
 * synchronously from firebase's cache when it is already in memory — the
 * common case for every component mounted after App.js resolved it — and the
 * async read only runs (and only re-renders) when the cache was empty or the
 * value's identity changed (the admin's forceRefresh path).
 *
 * @returns {object|null}
 */
const useCustomConfig = function () {
  const [config, setConfig] = useState(
    () => firebase.getCachedCustomizationConfig?.() ?? null,
  );

  useEffect(() => {
    let active = true;
    const fetchConfig = async () => {
      const customConfiguration = await firebase.getCustomizationConfig();
      // setState bails out when the identity is unchanged, so a cached hit
      // costs no render.
      if (active) setConfig(customConfiguration ?? null);
    };
    fetchConfig();
    return () => {
      active = false;
    };
  }, []);

  return config;
};

export default useCustomConfig;
